import { useMemo } from 'react';
import { useGetEstimateResultData } from './api/useGetEstimateResultData';
import { BODY_PAINT_STATE } from '@/consts/bodyAndPaint';
import {
  CarInformationItemType,
  EstimateDetailInfoType,
  EstimateDetailItemType,
  EstimateInfoType,
  EstimateResultDataType,
  InsuranceResultInfoType,
  RepairRequestIDInfoType,
} from '@/types/bodyAndPaint';

interface EstimateResultDataProps {
  status: number;
  affiliateID: number;
  customerID: number;
  inspectionID: number;
  saleID: number;
  carInfo: CarInformationItemType;
  checkInDate: Date;
}
export const useEstimateResultData = ({
  status,
  affiliateID,
  customerID,
  inspectionID,
  saleID,
  carInfo,
  checkInDate,
}: EstimateResultDataProps) => {
  const { estimateInfo, estimateItemList } = useGetEstimateResultData({
    m_customer_id: customerID,
    t_pm_car_inspection_id: inspectionID,
    t_sale_id: saleID,
  });
  // ID 情報
  const repairRequestIDInfo: RepairRequestIDInfoType = useMemo(() => {
    const idInfo = {
      affiliateID: affiliateID,
      bodyPaintID: estimateInfo?.t_body_paint_id ?? 0,
      customerID: customerID,
      estimateID: estimateInfo?.t_estimate_id ?? 0,
      inspectionID: inspectionID,
    };
    return idInfo;
  }, [affiliateID, customerID, inspectionID, estimateInfo]);
  // 保険利用フラグ 情報
  const isInsurance: boolean = useMemo(() => {
    return status === BODY_PAINT_STATE.APPLIES_RESULT;
  }, [status]);
  // 保険申請結果 情報
  const insuranceResultInfo: InsuranceResultInfoType | undefined =
    useMemo(() => {
      const estimateAmount = estimateInfo?.total_price ?? 0;
      const appliedAmount = estimateInfo?.total_confirmed_price ?? 0;
      const totalAmount =
        estimateAmount - appliedAmount + (estimateInfo?.deductible_fee ?? 0);
      const insuranceResult: InsuranceResultInfoType = {
        repair_estimate_amount: estimateAmount,
        insurance_applied_amount: appliedAmount,
        deductible_amount: estimateInfo?.deductible_fee,
        total_loss_determination: !!(
          estimateInfo?.total_loss_judge_flg ?? false
        ),
        total_repair_amount: totalAmount > 0 ? totalAmount : 0,
      };
      return isInsurance ? insuranceResult : undefined;
    }, [estimateInfo, isInsurance]);
  // 見積もりフラグ 情報
  const isEstimating: boolean = useMemo(() => {
    return (
      status !== BODY_PAINT_STATE.ESTIMATE_APPLY &&
      status !== BODY_PAINT_STATE.APPLIES_RESULT
    );
  }, [status]);
  // 見積もり結果詳細 情報
  const estimateDetailList: EstimateDetailInfoType = useMemo(() => {
    const estimateList = Array.isArray(estimateItemList)
      ? estimateItemList.reduce((list, estimate) => {
          const detailItem: EstimateDetailItemType = {
            id: estimate?.t_pm_car_inspection_need_maintenance_id ?? 0,
            name: estimate?.t_pm_car_inspection_need_maintenance_name ?? '',
            is_insurance: isInsurance
              ? estimate?.is_insurance_applicable
              : false,
            repair_amount: estimate?.subtotal ?? 0,
            applied_amount:
              (estimate?.subtotal ?? 0) -
              (estimate?.subtotal_insurance_price ?? 0),
          };
          list.push(detailItem);
          return list;
        }, [] as EstimateDetailItemType[])
      : ([] as EstimateDetailItemType[]);
    return estimateList;
  }, [estimateItemList, isInsurance]);
  // 見積もり 情報
  const estimateInformation: EstimateInfoType = useMemo(() => {
    const estimateNumber = `${estimateInfo?.unique_code ?? ''}-${
      estimateInfo?.estimate_version ?? ''
    }`;
    const estimateSubjects: EstimateInfoType = {
      created_at: new Date(estimateInfo?.issue_date ?? ''),
      estimate_number: estimateNumber,
      car_name: carInfo.car_name,
      number_plate: carInfo.registry_no,
      vin_number: carInfo.vin_number,
      check_in_date: new Date(checkInDate ?? ''),
    };
    return estimateSubjects;
  }, [carInfo, checkInDate, estimateInfo]);
  // 見積もり結果 情報
  const estimateResultData: EstimateResultDataType = useMemo(() => {
    const item: EstimateResultDataType = {
      ids: repairRequestIDInfo,
      isInsurance: isInsurance,
      insuranceResultInfo: insuranceResultInfo,
      isEstimating: isEstimating,
      estimateDetailList: estimateDetailList,
      estimateInfo: estimateInformation,
    };
    return item;
  }, [
    repairRequestIDInfo,
    isInsurance,
    isEstimating,
    insuranceResultInfo,
    estimateDetailList,
    estimateInformation,
  ]);
  return { estimateResultData };
};
